import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  RefreshCw,
  ShieldAlert,
  Check,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { EntityLink } from "../../components/ui/EntityLink";
import { imageReviewApi } from "../../utils/api";
import UrlThumbnail from "./UrlThumbnail";

const PAGE_SIZE = 25;

const formatTimestamp = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

export default function ImageBlocklistView() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [pendingUrl, setPendingUrl] = useState(null);
  // Separate from `error`, which means "the list failed to load" and also gates the
  // empty state — a per-row failure written there renders neither the list nor the
  // "Blocklist is empty" panel, just a red bar. Same split as ImageReviewView.
  // Carries its own tone: success and a capped-but-successful repair are not failures, and
  // painting either destructive-red reads as "the action failed", which it did not.
  const [notice, setNotice] = useState(null);

  const load = useCallback(async (pageNum) => {
    setLoading(true);
    setError(null);
    try {
      const data = await imageReviewApi.getBlocklist(pageNum, PAGE_SIZE);
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load blocklist.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [load, page]);

  // Releasing a blocked URL marks it a false positive rather than deleting the row. Deleting
  // it only removed the block: the very next scan re-ran the same heuristic, re-detected the
  // URL and demoted the image again, so the admin's decision never stuck. Same action and
  // same wording as the flagged list, so "this image is fine" means one thing everywhere.
  //
  // Refetch rather than splice the row out locally. Pagination is server-side by offset,
  // so dropping one row shifts every later entry up by one: the row that moves into this
  // page's last slot would never be rendered, and paging on would skip it entirely. The
  // reload also corrects `total` when the row was already gone (a second tab, say), which
  // a local decrement would silently undercount. Step back first if this emptied the page.
  const handleMarkOk = async (url) => {
    setPendingUrl(url);
    setNotice(null);
    try {
      const { capped } = await imageReviewApi.allowUrl(url);
      // A capped repair looks identical to a complete one in the row count, and re-running
      // cannot reach the remainder, so it has to be said out loud.
      setNotice({
        tone: capped ? "warning" : "info",
        text: capped
          ? "Marked OK, but too many events carry this image to restore in one pass. The rest keep the image chosen while it was blocked."
          : "Marked OK. It now sits under Flagged URLs, where it can be undone.",
      });
      const lastOnPage = entries.length === 1 && page > 1;
      if (lastOnPage) setPage((p) => p - 1);
      else await load(page);
    } catch (e) {
      setNotice({
        tone: "error",
        text: e?.response?.data?.detail || "Could not mark this image OK.",
      });
    } finally {
      setPendingUrl(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold">Confirmed placeholder images</h2>
          <p className="text-xs text-muted-foreground">
            URLs admins have marked as placeholders. Future events using these images are flagged automatically.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {total > 0 && (
            <span className="text-sm text-muted-foreground">{total} on blocklist</span>
          )}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => load(page)} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {[error && { tone: "error", text: error }, notice].filter(Boolean).map(({ tone, text }) => (
        <div
          key={text}
          className={`rounded-md px-4 py-2 text-sm ${
            tone === "warning"
              ? "bg-warning/10 text-warning"
              : tone === "info"
                ? "bg-muted text-muted-foreground"
                : "bg-destructive/10 text-destructive"
          }`}
        >
          {text}
        </div>
      ))}

      {loading && (
        <div className="flex items-center justify-center py-20 gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      )}

      {!loading && !entries.length && !error && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
          <ShieldAlert className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm font-medium">Blocklist is empty</p>
          <p className="text-xs">Resolving an image review will add its placeholder URLs here.</p>
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
          <ul className="divide-y divide-border text-xs">
            {entries.map((entry) => (
              <li key={entry.url} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-5 sm:py-2.5">
                <a href={entry.url} target="_blank" rel="noreferrer" title={entry.url}>
                  <UrlThumbnail url={entry.url} />
                </a>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-muted-foreground" title={entry.url}>{entry.url}</div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground/80">
                    Confirmed by <span className="font-medium">{entry.confirmed_by}</span>
                    {entry.confirmed_at && <> · {formatTimestamp(entry.confirmed_at)}</>}
                    {entry.source_event_id && (
                      <> · from event <EntityLink type="event" id={entry.source_event_id} newTab>{entry.source_event_id}</EntityLink></>
                    )}
                  </div>
                  {entry.note && (
                    <div className="mt-0.5 text-[10px] italic text-muted-foreground/80">“{entry.note}”</div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5"
                  disabled={Boolean(pendingUrl)}
                  onClick={() => handleMarkOk(entry.url)}
                >
                  {pendingUrl === entry.url
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Check className="h-3.5 w-3.5" />}
                  Not a placeholder
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!loading && <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
    </div>
  );
}
